// Activity and audit log (design system 06-modules §3): what happened, who did it, to what, when,
// with what outcome and from which surface. Newest first by default but sortable by actor and
// subject too (server-side, over the whole set); filters by actor, subject, operation, surface,
// outcome and period; the retention window stated on screen; each record linking to the entity it
// names; and `?highlight=` arriving with the row identified — or saying so when it is not loaded.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AuditEventDto, AuditListResponse } from '../../shared/api-types.js';
import { AUDIT_SURFACE_LABELS } from '../../shared/enums.js';
import { AdminSectionStrip } from '../shell/admin.js';
import { useMe } from '../shell/me-context.js';
import {
  Badge,
  Banner,
  Button,
  Chip,
  CopyButton,
  DataTable,
  Dialog,
  Page,
  PageHead,
  RelativeTime,
  Section,
  SelectField,
  Skeleton,
  StatusPill,
  TextField,
  browserTimeZone,
  formatCount,
  formatList,
  formatDateTime,
  inlineFailureText,
  pluralise,
  sortOptionsFor,
  timeZoneLabel,
  useIsMobile,
  useRequest,
  useUrlSort,
  type DataTableColumn,
  type DataTableStatus,
  type RequestStatus,
} from '../ui/index.js';
import { adminApi } from './admin-api.js';
import {
  EMPTY_AUDIT_FILTERS,
  OUTCOME_TONES,
  activeAuditFilterCount,
  auditFacets,
  auditQuery,
  auditTargetPath,
  describeAuditFilters,
  operationLabel,
  outcomeReason,
  outcomeWord,
  parseAuditFilters,
  resolveHighlight,
  splitActorLabel,
  targetTypeLabel,
  writeAuditFilters,
  type AuditFilters,
} from './admin-helpers.js';
import './admin.css';

const PAGE_SIZE = 200;
type AuditSortKey = 'created_at' | 'actor' | 'target' | 'operation' | 'outcome' | 'surface';
const DEFAULT_SORT = { key: 'created_at' as AuditSortKey, dir: 'desc' as const };

export default function AdminAuditPage() {
  const me = useMe();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const { filters, dropped } = useMemo(() => parseAuditFilters(params), [params]);
  const highlightParam = params.get('highlight');
  const timeZone = browserTimeZone();
  const zoneLabel = timeZoneLabel(timeZone);
  const can = { usersView: me.admin.users.view, groups: me.admin.groups, viewerId: me.subject.id };
  const [details, setDetails] = useState<AuditEventDto | null>(null);

  const setFilters = useCallback((next: AuditFilters) => {
    setParams((current) => writeAuditFilters(current, next), { replace: true });
  }, [setParams]);

  // Folding (04-data §1.7; ADR 0168): Surface and Outcome first, then the columns an access review sorts by (06-modules
  // §3.2) — Subject, Actor — and When, the record's own order, last. The operation names the row and Details is its
  // action: neither folds.
  const columns = useMemo<DataTableColumn<AuditEventDto>[]>(() => [
    {
      key: 'created_at',
      header: `When (${zoneLabel})`,
      name: 'When',
      type: 'date',
      card: 'meta',
      fold: 5,
      cell: (row) => <RelativeTime value={row.createdAt} mode="absolute" timeZone={timeZone} />,
    },
    {
      key: 'actor',
      header: 'Actor',
      fold: 4,
      cell: (row) => {
        const { name, email } = splitActorLabel(row.actor.label);
        const link = row.actor.id && can.usersView ? auditTargetPath({ type: 'subject', id: row.actor.id, label: row.actor.label }, can) : null;
        // Why "system" by the missing id and not by the missing address: a deleted account's rows read under its
        // pseudonym, with no address (owner ruling O4 of 2026-10-05), and are still a person's acts — only a row with
        // no actor id is the system's. The pseudonym says what it is; nothing is put beneath it.
        const kind = email ?? (row.actor.id ? null : 'system');
        return (
          <span className="audit-actor">
            {link ? <Link to={link} className="hit-area">{name}</Link> : <span>{name}</span>}
            {kind ? <span className="audit-actor__email">{kind}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'target',
      header: 'Subject',
      fold: 3,
      cell: (row) => {
        const link = auditTargetPath(row.target, can, row.actor);
        const label = row.target.label ?? (row.target.id ? `${targetTypeLabel(row.target.type)} ${row.target.id.slice(0, 8)}…` : targetTypeLabel(row.target.type));
        return (
          <span className="audit-actor">
            {link ? <Link to={link} className="hit-area">{label}</Link> : <span>{label}</span>}
            <span className="audit-target__type">{targetTypeLabel(row.target.type)}</span>
          </span>
        );
      },
    },
    {
      key: 'operation',
      header: 'Operation',
      card: 'title',
      // Why the key is not shown here: the stored name is not the reader's word (05-content §3); it
      // stays in the Operation filter's options and in Record details for anyone who needs it. Why the row's `after`: a row
      // a deletion or a merge wrote in passing reads as what was done, by its reason (`operationLabel`; PM-9's endings).
      cell: (row) => operationLabel(row.operation, row.after),
    },
    { key: 'outcome', header: 'Outcome', type: 'enum', fold: 2, cell: (row) => <OutcomeWithReason record={row} /> },
    { key: 'surface', header: 'Surface', fold: 1, cell: (row) => AUDIT_SURFACE_LABELS[row.surface] },
    {
      key: 'details',
      header: <span className="visually-hidden">Details</span>,
      name: 'Details',
      sortable: false,
      align: 'end',
      card: 'actions',
      cell: (row) => <Button size="sm" variant="ghost" onClick={() => setDetails(row)}>Details</Button>,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [zoneLabel, timeZone, can.usersView, can.groups]);

  const [sort] = useUrlSort<AuditSortKey>(sortOptionsFor(columns, { defaultSort: DEFAULT_SORT }));

  // The first page comes through useRequest (cancellation, states); later pages are appended.
  const filterKey = JSON.stringify(filters);
  const first = useRequest<AuditListResponse>((signal) => adminApi.audit(auditQuery(filters, sort, { limit: PAGE_SIZE, offset: 0 }), signal), [filterKey, sort.key, sort.dir]);
  const [more, setMore] = useState<AuditEventDto[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailure, setMoreFailure] = useState<string | null>(null);
  useEffect(() => { setMore([]); setMoreFailure(null); }, [filterKey, sort.key, sort.dir]);

  const items = useMemo(() => [...(first.data?.items ?? []), ...more], [first.data, more]);
  const total = first.data?.total ?? 0;
  const unfilteredTotal = first.data?.unfilteredTotal ?? 0;
  const retentionDays = first.data?.retentionDays ?? null;
  const facets = useMemo(() => auditFacets(items, filters), [items, filters]);

  const loadMore = async (): Promise<void> => {
    setLoadingMore(true);
    setMoreFailure(null);
    const result = await adminApi.audit(auditQuery(filters, sort, { limit: PAGE_SIZE, offset: items.length }));
    setLoadingMore(false);
    if (!result.ok) {
      setMoreFailure(inlineFailureText(result));
      return;
    }
    setMore((current) => [...current, ...result.data.items]);
  };

  // Highlight (06-modules §1.9): the id from the link, or the newest loaded record for `latest`.
  const highlightId = useMemo(() => resolveHighlight(highlightParam, items), [highlightParam, items]);
  const [highlightMissing, setHighlightMissing] = useState<string | null>(null);
  useEffect(() => { setHighlightMissing(null); }, [highlightParam, filterKey]);

  const status: DataTableStatus = first.status === 'loading'
    ? { kind: 'loading', rowCount: 8 }
    : first.status === 'error' && first.error
      ? { kind: 'error', failure: first.error, onRetry: first.reload }
      : { kind: 'ready' };

  const filtering = activeAuditFilterCount(filters) > 0;
  const clear = () => setFilters({ ...EMPTY_AUDIT_FILTERS });

  // Why both figures while filtering, as the roster's "Accounts — N of M" (04-data §1.5): one number cannot tell a narrow
  // filter from a small log. Both are the server's, under the same reader bound. Why no figure once the request failed:
  // what is held then is the previous answer or none, and a stale or zero count beside an error reads as a fact.
  const recordsHeading = first.status === 'loading'
    ? <span className="count-heading">Records <Skeleton width="2rem" className="count-heading__skeleton" /></span>
    : first.status === 'error' || !first.data
      ? 'Records'
      : recordsCountLine(filtering, total, unfilteredTotal, 'heading');

  return (
    <Page measure="wide">
      <PageHead title="Audit log" strip={<AdminSectionStrip />} />

      {retentionDays === null ? null : (
        <p className="secondary text-sm" role="status">
          {`Records are kept for ${pluralise(retentionDays, { one: '# day', other: '# days' })}.`}
        </p>
      )}

      {dropped.length > 0 ? (
        <Banner tone="warning" title="Some filters in the link were not understood">
          {`Ignored: ${formatList(dropped)}. The other filters still apply.`}
        </Banner>
      ) : null}

      {highlightMissing ? (
        <Banner tone="info" title="The linked record is not on this page" onDismiss={() => setHighlightMissing(null)}>
          {highlightParam === 'latest'
            ? 'No record is loaded to highlight.'
            : `Record ${highlightMissing} is not among the ${formatCount(items.length)} loaded. It may be outside the current filters, further down the list, or older than the retention window.`}
          {filtering ? <> <Button variant="link" size="sm" onClick={clear}>Clear the filters</Button></> : null}
        </Banner>
      ) : null}

      <Section id="records" title={recordsHeading}>
        <AuditFilterBar filters={filters} facets={facets} onChange={setFilters} onClear={clear} isMobile={isMobile} status={first.status} total={total} unfilteredTotal={unfilteredTotal} />

        <DataTable<AuditEventDto, AuditSortKey>
          caption="Audit log. The timestamp column is the record's own order."
          failureNoun="the audit log"
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          status={status}
          sort={{ defaultSort: DEFAULT_SORT }}
          highlightId={highlightId}
          onHighlightMissing={(id) => setHighlightMissing(id)}
          empty={{
            title: 'No activity recorded',
            description: 'Every sign-in is recorded, so an empty log usually means it could not be read.',
            action: <Button variant="secondary" icon="refresh" onClick={first.reload}>Try again</Button>,
          }}
          filtered={{ active: filtering, onClear: clear, title: 'No records match these filters', description: describeAuditFilters(filters, facets).map((chip) => chip.label).join(' · ') }}
          footer={first.data ? (
            // The loaded position against the matching count (03-components §11); the heading carries the two figures.
            <div className="audit-footer">
              <span>{`Showing ${formatCount(items.length)} of ${pluralise(total, { one: '# record', other: '# records' })}`}</span>
              {items.length < total ? (
                <Button variant="secondary" onClick={() => void loadMore()} loading={loadingMore}>{`Load ${formatCount(Math.min(PAGE_SIZE, total - items.length))} more`}</Button>
              ) : null}
              {moreFailure ? <span role="alert">{moreFailure}</span> : null}
            </div>
          ) : undefined}
        />
      </Section>

      <Dialog open={details !== null} onClose={() => setDetails(null)} title="Record details" size="lg">
        {details ? <AuditDetails record={details} timeZone={timeZone} /> : null}
      </Dialog>
    </Page>
  );
}

/**
 * The log's count, written once for the heading and the live region so the two cannot drift. Filtered: both figures,
 * the matching count of this reader's whole log, in the roster's form (06-modules §1.3 R2, 04-data §1.5). Unfiltered:
 * the whole log alone.
 */
function recordsCountLine(filtering: boolean, total: number, unfilteredTotal: number, form: 'heading' | 'announcement'): string {
  if (filtering) return `Records — ${formatCount(total)} of ${formatCount(unfilteredTotal)}`;
  return form === 'heading' ? `Records (${formatCount(unfilteredTotal)})` : pluralise(unfilteredTotal, { one: '# record', other: '# records' });
}

// --- Filter bar --------------------------------------------------------------------------------------

interface AuditFilterBarProps {
  filters: AuditFilters;
  facets: ReturnType<typeof auditFacets>;
  onChange: (next: AuditFilters) => void;
  onClear: () => void;
  isMobile: boolean;
  /** The first page's request: its figures are announced only once it has answered. */
  status: RequestStatus;
  total: number;
  unfilteredTotal: number;
}

/**
 * Structured filters only — the log has no free-text search, because its columns are identifiers
 * and closed vocabularies. Actor, subject, kind and operation are drawn from the loaded records;
 * surface and outcome are the closed enums. Period is two date fields. Applied on change on
 * desktop; below --bp-md they live in a sheet with an explicit Apply, the chip rail staying outside.
 */
function AuditFilterBar({ filters, facets, onChange, onClear, isMobile, status, total, unfilteredTotal }: AuditFilterBarProps) {
  const loading = status === 'loading';
  const [sheetOpen, setSheetOpen] = useState(false);
  const [draft, setDraft] = useState<AuditFilters>(filters);
  const periodTimer = useRef<number | null>(null);
  useEffect(() => () => { if (periodTimer.current !== null) window.clearTimeout(periodTimer.current); }, []);

  const activeCount = activeAuditFilterCount(filters);
  const chips = describeAuditFilters(filters, facets);
  const remove = (key: string): void => {
    if (key === 'period') onChange({ ...filters, from: '', to: '' });
    else if (key in filters) onChange({ ...filters, [key]: '' });
  };

  const fields = (value: AuditFilters, set: (next: AuditFilters) => void, immediate: boolean) => {
    // Why: a date field fires change on every keystroke in some browsers; the period waits ~300 ms
    // before it refetches so a half-typed year does not produce a queue of results.
    const setPeriod = (next: AuditFilters): void => {
      if (!immediate) { set(next); return; }
      if (periodTimer.current !== null) window.clearTimeout(periodTimer.current);
      periodTimer.current = window.setTimeout(() => set(next), 300);
    };
    return (
      <>
        <SelectField className="filter-bar__facet" label="Actor" value={value.actor} onChange={(event) => set({ ...value, actor: event.target.value })} options={facets.actors} placeholderOption="Anyone" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Subject" value={value.target} onChange={(event) => set({ ...value, target: event.target.value })} options={facets.targets} placeholderOption="Anything" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Kind" value={value.targetType} onChange={(event) => set({ ...value, targetType: event.target.value })} options={facets.targetTypes} placeholderOption="Any kind" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Operation" value={value.operation} onChange={(event) => set({ ...value, operation: event.target.value })} options={facets.operations} placeholderOption="Any operation" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Surface" value={value.surface} onChange={(event) => set({ ...value, surface: event.target.value })} options={facets.surfaces} placeholderOption="Any surface" disabled={loading} />
        <SelectField className="filter-bar__facet" label="Outcome" value={value.outcome} onChange={(event) => set({ ...value, outcome: event.target.value })} options={facets.outcomes} placeholderOption="Any outcome" disabled={loading} />
        <TextField className="filter-bar__period" label="From" type="date" value={value.from} max={value.to || undefined} onChange={(event) => setPeriod({ ...value, from: event.target.value })} disabled={loading} />
        <TextField className="filter-bar__period" label="Until" type="date" value={value.to} min={value.from || undefined} hint="Whole days, both ends included" onChange={(event) => setPeriod({ ...value, to: event.target.value })} disabled={loading} />
      </>
    );
  };

  return (
    <div className="filter-bar">
      <div className="filter-bar__controls">
        {isMobile ? (
          <Button className="filter-bar__trigger" variant="secondary" icon="filter" onClick={() => { setDraft(filters); setSheetOpen(true); }} aria-label={activeCount > 0 ? `Filters, ${activeCount} active` : 'Filters'}>
            Filters{activeCount > 0 ? <> <Badge tone="accent">{String(activeCount)}</Badge></> : null}
          </Button>
        ) : fields(filters, onChange, true)}
      </div>
      <div className="filter-bar__active">
        {/* Why hidden: the section heading already carries the count and, filtered, both figures (04-data §1.5), as the
            roster's does; the live region still announces them (03-components §10). Nothing is announced for a failed
            request — the table's error state says what happened. */}
        <p className="filter-bar__count visually-hidden" role="status" aria-live="polite">
          {loading ? 'Loading records…' : status === 'error' ? '' : recordsCountLine(activeCount > 0, total, unfilteredTotal, 'announcement')}
        </p>
        {chips.length > 0 ? (
          // Why a named list: the to-dos filter bar groups its chips the same way, so assistive
          // technology announces "Active filters" identically on every filtered screen (03-components §10).
          <ul className="cluster" role="list" aria-label="Active filters">
            {chips.map((chip) => (
              <li key={chip.key}>
                <Chip selected onRemove={() => remove(chip.key)} removeLabel={`Remove filter: ${chip.label}`}>{chip.label}</Chip>
              </li>
            ))}
            <li>
              <Button variant="link" size="sm" onClick={onClear}>Clear all</Button>
            </li>
          </ul>
        ) : null}
      </div>
      {isMobile ? (
        <Dialog
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Filters"
          size="sm"
          initialFocus="first"
          actions={
            <>
              <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button variant="primary" onClick={() => { onChange(draft); setSheetOpen(false); }}>Apply</Button>
            </>
          }
        >
          <div className="stack stack--3">{fields(draft, setDraft, false)}</div>
        </Dialog>
      ) : null}
    </div>
  );
}

// --- Outcome ---------------------------------------------------------------------------------------

/** A record's Outcome word, and — for one that did not go ahead and says why — its reason beneath (05-content §6). */
function OutcomeWithReason({ record }: { record: Pick<AuditEventDto, 'outcome' | 'after'> }) {
  const reason = outcomeReason(record);
  return (
    <span className="audit-outcome">
      <StatusPill tone={OUTCOME_TONES[record.outcome]}>{outcomeWord(record.outcome)}</StatusPill>
      {reason ? <span className="audit-outcome__reason">{reason}</span> : null}
    </span>
  );
}

// --- Record details ----------------------------------------------------------------------------------

function AuditDetails({ record, timeZone }: { record: AuditEventDto; timeZone: string }) {
  const json = (value: unknown): string => (value === null || value === undefined ? '—' : JSON.stringify(value, null, 2));
  const actor = splitActorLabel(record.actor.label);
  return (
    <div className="stack">
      <dl className="field-list">
        <dt>When</dt>
        <dd>{formatDateTime(record.createdAt, { timeZone })}</dd>
        <dt>Actor</dt>
        <dd>{actor.email ? `${actor.name} · ${actor.email}` : actor.name}</dd>
        <dt>Operation</dt>
        <dd>{operationLabel(record.operation, record.after)} <code className="selectable text-xs">{record.operation}</code></dd>
        <dt>Subject</dt>
        <dd>{record.target.label ?? '—'} <span className="text-xs muted">{targetTypeLabel(record.target.type)}{record.target.id ? <> · <code className="selectable">{record.target.id}</code></> : null}</span></dd>
        <dt>Outcome</dt>
        <dd><OutcomeWithReason record={record} /></dd>
        <dt>Surface</dt>
        <dd>{AUDIT_SURFACE_LABELS[record.surface]}</dd>
        <dt>Record id</dt>
        <dd><code className="selectable text-xs">{record.id}</code> <CopyButton value={record.id} label="Copy record id" /></dd>
        <dt>Trace id</dt>
        <dd>{record.traceId ? <><code className="selectable text-xs">{record.traceId}</code> <CopyButton value={record.traceId} label="Copy trace id" /></> : '—'}</dd>
      </dl>
      <div className="stack stack--2">
        <h3>Before</h3>
        <pre className="audit-details__json">{json(record.before)}</pre>
        <h3>After</h3>
        <pre className="audit-details__json">{json(record.after)}</pre>
      </div>
    </div>
  );
}
